import { createContext, useEffect, useMemo, useState } from 'react'
import useLocalStorageState from 'use-local-storage-state'

import { createUseContext } from '@lib/react'

const ALLOWED_BRANDS = ['pmo', 'carve-out', 'd-m', 'haniel', 'accuracy', 'portfolex', 'steerex', 'postxl'] as const

export type ColorMode = 'dark' | 'light' | 'system'
export type Brand = (typeof ALLOWED_BRANDS)[number]
export type Theme = `${Brand} ${ColorMode}`
export type ResolvedColorMode = 'dark' | 'light'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultBrand?: Brand
  defaultColorMode?: ColorMode
  storageKey?: string
}

type ThemeProviderState = {
  brand: Brand
  colorMode: ColorMode
  resolvedColorMode: ResolvedColorMode
  setColorMode: (colorMode: ColorMode) => void
  setBrand: (brand: Brand) => void
}

type ThemeStorage = {
  brand: Brand
  colorMode: ColorMode
}

const initialState: ThemeProviderState = {
  brand: 'pmo',
  colorMode: 'system',
  resolvedColorMode: 'light',
  setColorMode: () => null,
  setBrand: () => null,
}

const ThemeContext = createContext<ThemeProviderState>(initialState)
const STORAGE_KEY = 'steerex-theme-mode'

export function ThemeProvider({
  children,
  defaultBrand = 'pmo',
  defaultColorMode = 'system',
  ...props
}: Readonly<ThemeProviderProps>) {
  const [themeStorage, setThemeStorage] = useLocalStorageState<ThemeStorage>(STORAGE_KEY, {
    defaultValue: { brand: defaultBrand, colorMode: defaultColorMode },
  })
  const { brand, colorMode } = themeStorage

  const resolveColorMode = (mode: ColorMode): ResolvedColorMode =>
    mode === 'system' ? (globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : mode

  const [resolvedColorMode, setResolvedColorMode] = useState<ResolvedColorMode>(() => resolveColorMode(colorMode))

  useEffect(() => {
    const resolved = resolveColorMode(colorMode)
    setResolvedColorMode(resolved)

    const root = globalThis.document.documentElement
    root.classList.remove('light', 'dark', ...ALLOWED_BRANDS)
    root.classList.add(brand, resolved)

    if (colorMode !== 'system') {
      return
    }

    // React to OS theme changes while "system" is selected (passive listener, no polling)
    const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const newResolved: ResolvedColorMode = e.matches ? 'dark' : 'light'
      setResolvedColorMode(newResolved)
      root.classList.remove('light', 'dark')
      root.classList.add(newResolved)
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [brand, colorMode])

  const value = useMemo(
    () => ({
      brand,
      colorMode,
      resolvedColorMode,
      setColorMode: (newColorMode: ColorMode) => {
        setThemeStorage({ brand, colorMode: newColorMode })
      },
      setBrand: (newBrand: Brand) => {
        setThemeStorage({ brand: newBrand, colorMode })
      },
    }),
    [brand, colorMode, resolvedColorMode, setThemeStorage],
  )

  return (
    <ThemeContext.Provider {...props} value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = createUseContext(ThemeContext, 'ThemeProviderContext')
